import { useState, useEffect } from 'react';
import { api } from '../api.js';
import Spinner from '../components/Spinner.jsx';

const ROLE_STYLES = {
  admin:     'bg-red-100 text-red-700',
  ausbilder: 'bg-blue-100 text-blue-700',
  schueler:  'bg-gray-100 text-gray-600',
};
const ROLE_LABELS = { admin: 'Admin', ausbilder: 'Ausbilder', schueler: 'Schüler' };

function RoleBadge({ role }) {
  return (
    <span className={`badge ${ROLE_STYLES[role] ?? 'bg-gray-100 text-gray-500'}`}>
      {ROLE_LABELS[role] ?? role}
    </span>
  );
}

function NewUserModal({ onClose, onCreated }) {
  const [form, setForm]   = useState({ email: '', role: 'schueler', display_name: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [warning, setWarning] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const res = await api.adminCreateUser(form);
      onCreated();
      if (res?.warning) { setWarning(res.warning); setSaving(false); return; }
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="card max-w-sm w-full space-y-4">
        <h2 className="font-bold text-gray-900">Neuen Benutzer anlegen</h2>
        {warning && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <strong>Benutzer angelegt, aber:</strong> {warning}
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label">E-Mail *</label>
            <input type="email" className="input" required
              value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div>
            <label className="label">Rolle *</label>
            <select className="input" value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              <option value="schueler">Schüler</option>
              <option value="ausbilder">Ausbilder</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="label">Anzeigename (optional)</label>
            <input className="input" value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="btn-secondary">Abbrechen</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Anlegen…' : 'Anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null); // email being edited
  const [saving, setSaving]   = useState(null);

  const load = () => {
    setLoading(true);
    api.adminGetUsers()
      .then(setUsers)
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const update = async (email, patch) => {
    setSaving(email);
    try {
      await api.adminUpdateUser(email, patch);
      load();
      setEditing(null);
    } catch (err) {
      alert(err.message);
    } finally { setSaving(null); }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Benutzer "${email}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    setSaving(email);
    try {
      await api.adminDeleteUser(email);
      load();
      setEditing(null);
    } catch (err) {
      alert(err.message);
    } finally { setSaving(null); }
  };

  const groups = {
    admin:     users.filter((u) => u.role === 'admin'),
    ausbilder: users.filter((u) => u.role === 'ausbilder'),
    schueler:  users.filter((u) => u.role === 'schueler'),
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Benutzerverwaltung</h1>
        <button onClick={() => setShowNew(true)} className="btn-primary text-xs">
          + Benutzer anlegen
        </button>
      </div>

      {loading && <div className="flex justify-center py-12"><Spinner size="lg" /></div>}

      {!loading && Object.entries(groups).map(([role, roleUsers]) => (
        roleUsers.length === 0 ? null : (
          <div key={role} className="space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-2">
              <RoleBadge role={role} />
              <span>{roleUsers.length} Benutzer</span>
            </h2>
            {roleUsers.map((u) => (
              <div key={u.email} className={`card ${u.status === 'inactive' ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900">
                        {u.display_name || u.email}
                      </span>
                      <RoleBadge role={u.role} />
                      {u.status === 'inactive' && (
                        <span className="badge bg-gray-100 text-gray-400">inaktiv</span>
                      )}
                    </div>
                    {u.display_name && (
                      <p className="text-xs text-gray-400 mt-0.5">{u.email}</p>
                    )}
                    {u.invited_at && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Eingeladen {u.invited_at.slice(0, 10)}
                        {u.invited_by ? ` von ${u.invited_by.split('@')[0]}` : ''}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setEditing(editing === u.email ? null : u.email)}
                    className="btn-ghost text-xs shrink-0"
                    aria-expanded={editing === u.email}
                    aria-label={editing === u.email
                      ? `Bearbeitung von ${u.email} schließen`
                      : `${u.email} bearbeiten`}
                  >
                    <span aria-hidden="true">{editing === u.email ? '✕' : '⚙'}</span>
                  </button>
                </div>

                {editing === u.email && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    <p className="text-xs font-medium text-gray-500">Rolle ändern</p>
                    <div className="flex gap-2 flex-wrap">
                      {['admin', 'ausbilder', 'schueler'].map((r) => (
                        <button key={r} disabled={r === u.role || saving === u.email}
                          onClick={() => update(u.email, { role: r })}
                          className={`btn text-xs ${r === u.role ? 'opacity-40 cursor-default bg-gray-100 text-gray-500' : 'btn-secondary'}`}>
                          <RoleBadge role={r} />
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => update(u.email, { status: u.status === 'active' ? 'inactive' : 'active' })}
                        disabled={saving === u.email}
                        className={u.status === 'active' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}
                      >
                        {saving === u.email ? '…' : u.status === 'active' ? 'Deaktivieren' : 'Reaktivieren'}
                      </button>
                      <button
                        onClick={() => deleteUser(u.email)}
                        disabled={saving === u.email}
                        className="btn-danger text-xs ml-auto"
                      >
                        🗑 Löschen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ))}

      {!loading && users.length === 0 && (
        <div className="card text-center text-gray-400 py-10 text-sm">Keine Benutzer gefunden.</div>
      )}

      {showNew && (
        <NewUserModal onClose={() => setShowNew(false)} onCreated={load} />
      )}
    </div>
  );
}
